import {
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  type ElementSizingMode,
  type IconSelectOption,
  ITEMS_CONTAINER_ACTION_ADD_CONVERSATION,
  ITEMS_CONTAINER_ACTION_EDIT_CONVERSATION,
  ITEMS_CONTAINER_ACTION_LEAVE_CONVERSATION,
  ITEMS_CONTAINER_ACTION_SEND_MESSAGE,
  type PropertiesConfig,
  type PropertiesConfigInputOptionsToggle,
} from '@blaze-shared/elements-domain';
import { COLUMN_ID_OR_NAME, DEFAULT_SCHEMA } from '@blaze-shared/schema-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';
import {
  validateFontSizeWithLineHeight,
  validateLineHeightWithFontSize,
} from '../../utils/elements/validation.utils';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { ChatComponent } from './chat/chat.component';
import { CHAT_DEFAULTS, type ChatPropsModel } from './chat-element.models';
import { ChatPropsExtractor } from './chat.props-extractor';
import { chatV2Migration } from './migrations/002-chat.migration';
import { chatV3Migration } from './migrations/003-chat.migration';

export type ChatDef = WithProps<ChatPropsModel> & WithIoConfig;

declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    chat: ChatDef;
  }
}

const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed', 'auto'];

registerElementDefinition({
  type: 'chat',
  componentType: ChatComponent,
  isListed: true,
  name: 'Chat',
  width: { mode: 'value', value: 650, fixed: true },
  height: { mode: 'value', value: 450, fixed: true },
  propsConfig: getPropsConfig(),
  propsExtractor: ChatPropsExtractor,
  defaults: getDefaults(),
  ioConfig: getIoConfig(),
  initial: getInitialValues(),
  groups: ['advanced'],
  styleIncludePaths: ['style', 'content.title.align', 'content.title.iconPosition'],

  version: 3,
  migrations: {
    2: chatV2Migration,
    3: chatV3Migration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'messages',
        bindingDefs: [],
        isNonBlockingInput: true,
        isQueryable: true,
      },
      {
        name: 'conversations',
        bindingDefs: [],
      },
      {
        name: 'conversationsInfo',
        bindingDefs: [],
      },
      {
        name: 'members',
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      { eventType: 'chat-conversation-select', schema: DEFAULT_SCHEMA },
      { eventType: 'query', schema: null },
      { eventType: 'action-chat-message-sent', schema: DEFAULT_SCHEMA },
      { eventType: 'action-chat-conversation-added', schema: DEFAULT_SCHEMA },
      { eventType: 'action-chat-conversation-edited', schema: DEFAULT_SCHEMA },
      // { eventType: 'action-chat-conversation-deleted', schema: DEFAULT_SCHEMA },
      { eventType: 'action-chat-conversation-left', schema: DEFAULT_SCHEMA },
    ],
  };
}

function getDefaults(): PropsOf<'chat'> {
  return CHAT_DEFAULTS;
}

function getInitialValues(): PartialPropsOf<'chat'> {
  return {
    version: 2,
    content: {
      messageAlignment: 'split',
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('chat', [
    epc.section('content', 'Content', [
      epc.subsections.dataSubsection(
        'data.?key:messages.enabled',
        'Data',
        [],
        [
          epc.fields.selectDataSourceField(
            {
              key: 'messages',
              label: 'Messages',
              sources: 'list',
              bindings: [],
            },
            'normal',
            'Messages',
            {
              hideAiButton: true,
            },
          ),
          ...epc.utils.conditionalFields(
            [
              epc.fields.selectFieldFromDataBinding('content.input.messages.id', 'ID', 'messages', {
                dataBindingsMode: 'first-dataset',
                valueField: COLUMN_ID_OR_NAME,
                hideLabel: false,
                optionsFilter: epc.utils.columnDataTypeFilter('Number'),
                labelWidth: '100px',
              }),
              epc.fields.selectFieldFromDataBinding(
                'content.input.messages.messageBody',
                'Message Body',
                'messages',
                {
                  dataBindingsMode: 'first-dataset',
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('String'),
                  labelWidth: '100px',
                },
              ),
              epc.fields.selectFieldFromDataBinding(
                'content.input.messages.createdTime',
                'Created Time',
                'messages',
                {
                  dataBindingsMode: 'first-dataset',
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('DateTime'),
                  labelWidth: '100px',
                },
              ),
              epc.fields.selectFieldFromDataBinding(
                'content.input.messages.updatedTime',
                'Updated Time',
                'messages',
                {
                  dataBindingsMode: 'first-dataset',
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('DateTime'),
                  labelWidth: '100px',
                },
              ),
              epc.fields.selectFieldFromDataBinding(
                'content.input.messages.modifiedTime',
                'Modified Time',
                'messages',
                {
                  dataBindingsMode: 'first-dataset',
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('DateTime'),
                  labelWidth: '100px',
                },
              ),
              epc.fields.selectFieldFromDataBinding(
                'content.input.messages.sender',
                'Sender',
                'messages',
                {
                  dataBindingsMode: 'first-dataset',
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('String'),
                  labelWidth: '100px',
                },
              ),
              epc.fields.selectFieldFromDataBinding(
                'content.input.messages.conversation',
                'Conversation',
                'messages',
                {
                  dataBindingsMode: 'first-dataset',
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('Link'),
                  labelWidth: '100px',
                },
              ),
              epc.fields.selectFieldFromDataBinding(
                'content.input.messages.attachments',
                'Attachments',
                'messages',
                {
                  dataBindingsMode: 'first-dataset',
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('Image', 'File'),
                  labelWidth: '100px',
                },
              ),
            ],
            'data.?key:messages.dataSource',
            (dataSourceSelected) => !dataSourceSelected,
          ),
          epc.fields.selectDataSourceField(
            {
              key: 'members',
              label: 'Members',
              sources: 'list',
              bindings: [],
            },
            'normal',
            'Members',
            {
              hideAiButton: true,
            },
          ),
          ...epc.utils.conditionalFields(
            [
              epc.fields.selectFieldFromDataBinding(
                'content.input.members.status',
                'Status',
                'members',
                {
                  dataBindingsMode: 'first-dataset',
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  allowNone: true,
                  optionsFilter: epc.utils.columnDataTypeFilter('Tag'),
                },
              ),
              epc.fields.selectFieldFromDataBinding(
                'content.input.members.statusDescription',
                'Status Description',
                'members',
                {
                  dataBindingsMode: 'first-dataset',
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  allowNone: true,
                  optionsFilter: epc.utils.columnDataTypeFilter('String'),
                },
              ),
            ],
            'data.?key:members.dataSource',
            (dataSourceSelected) => !dataSourceSelected,
          ),
          epc.fields.selectDataSourceField(
            {
              key: 'conversations',
              label: 'Conversations',
              sources: 'list',
              bindings: [],
            },
            'normal',
            'Conversations',
            {
              hideAiButton: true,
            },
          ),
          ...epc.utils.conditionalFields(
            [
              epc.fields.selectFieldFromDataBinding(
                'content.input.conversations.name',
                'Name',
                'conversations',
                {
                  dataBindingsMode: 'first-dataset',
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('String'),
                  labelWidth: '100px',
                },
              ),
              epc.fields.selectFieldFromDataBinding(
                'content.input.conversations.id',
                'ID',
                'conversations',
                {
                  dataBindingsMode: 'first-dataset',
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('Number'),
                  labelWidth: '100px',
                },
              ),
              epc.fields.selectFieldFromDataBinding(
                'content.input.conversations.members',
                'Members',
                'conversations',
                {
                  dataBindingsMode: 'first-dataset',
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('Link'),
                  labelWidth: '100px',
                },
              ),
              epc.fields.selectFieldFromDataBinding(
                'content.input.conversations.createdBy',
                'Created by',
                'conversations',
                {
                  dataBindingsMode: 'first-dataset',
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('String'),
                  labelWidth: '100px',
                },
              ),
            ],
            'data.?key:conversations.dataSource',
            (dataSourceSelected) => !dataSourceSelected,
          ),
          epc.fields.selectDataSourceField(
            {
              key: 'conversationsInfo',
              label: 'Conversations Info',
              sources: 'list',
              bindings: [],
            },
            'normal',
            'Conversations Info',
            {
              hideAiButton: true,
            },
          ),
          ...epc.utils.conditionalFields(
            [
              epc.fields.selectFieldFromDataBinding(
                'content.input.conversationsInfo.conversation',
                'Conversation',
                'conversationsInfo',
                {
                  dataBindingsMode: 'first-dataset',
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('Link'),
                  labelWidth: '100px',
                },
              ),
              epc.fields.selectFieldFromDataBinding(
                'content.input.conversationsInfo.lastMessageId',
                'Last Message',
                'conversationsInfo',
                {
                  dataBindingsMode: 'first-dataset',
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('Number'),
                  labelWidth: '100px',
                },
              ),
              epc.fields.selectFieldFromDataBinding(
                'content.input.conversationsInfo.membersName',
                'Member',
                'conversationsInfo',
                {
                  dataBindingsMode: 'first-dataset',
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('Link'),
                  labelWidth: '100px',
                },
              ),
              epc.fields.selectFieldFromDataBinding(
                'content.input.conversationsInfo.membersEmail',
                "Member's Email",
                'conversationsInfo',
                {
                  dataBindingsMode: 'first-dataset',
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('String'),
                  labelWidth: '100px',
                },
              ),
              epc.fields.selectFieldFromDataBinding(
                'content.input.conversationsInfo.membersImage',
                "Member's image",
                'conversationsInfo',
                {
                  dataBindingsMode: 'first-dataset',
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('Image'),
                  labelWidth: '100px',
                },
              ),
            ],
            'data.?key:conversationsInfo.dataSource',
            (dataSourceSelected) => !dataSourceSelected,
          ),
        ],
        { enabledByDefault: true },
      ),
      epc.subsections.subsection('content', 'Options', [
        epc.fields.toggleField('content.enableMessageEdit', 'Enable message editing'),
        epc.fields.toggleField('content.showConversationsMenu', 'Show conversations menu'),
        epc.fields.toggleField('content.showConversationFilters', 'Show conversation filters', {
          hideWhen: (model) => {
            return !getValueByPath(model, 'content.showConversationsMenu');
          },
        }),
        epc.fields.toggleField(
          'content.conversationsManagementEnabled',
          'Allow users to manage conversations',
          {
            tooltip:
              'When off, app users can’t add conversations, edit conversations, or leave them.',
            hideWhen: (model) => {
              return !getValueByPath(model, 'content.showConversationsMenu');
            },
          },
        ),
        epc.utils.separator('Sender vs. Recipient Message Alignment'),
        epc.fields.radiosField(
          'content.messageAlignment',
          '',
          [
            { value: 'left', label: 'All left' },
            { value: 'split', label: 'Split' },
          ],
          { hideLabel: true },
        ),
      ]),
      epc.subsections.subsection(
        'content.intro',
        'Intro',
        [
          epc.fields.listChatIntroMessagesFields(`content.intro.messages`, 'Intro Message'),
          epc.utils.conditionalField(
            epc.utils.separator(),
            'content.intro.messages',
            (messages: IconSelectOption[]) => !messages?.length,
          ),
          epc.fields.toggleField('content.intro.showImage', 'Add image (optional)'),
          epc.utils.conditionalField(
            epc.fields.uploadField('content.intro.image', '', { note: 'Type URL or upload image' }),
            'content.intro.showImage',
            (enabled: boolean) => !enabled,
          ),
        ],
        { hideLabel: true },
      ),
      epc.subsections.subsection(
        'content',
        'Actions',
        [
          epc.fields.textInfo('', 'Enable an event to set up additional workflow', {
            classes: ['!-mt-16', 'py-4'],
          }),
          epc.utils.adornField<'toggle', PropertiesConfigInputOptionsToggle>(
            epc.fields.toggleField(
              'content.actions.sendMessage.enabled',
              'After sending a message',
            ),
            {
              action: () => ({
                id: ITEMS_CONTAINER_ACTION_SEND_MESSAGE,
                inputBindingKey: 'messages',
              }),
              workflowId: (model: PropsOf<'chat'>) => model.content?.actions?.sendMessage?.targetId,
            },
          ),
          epc.utils.adornField<'toggle', PropertiesConfigInputOptionsToggle>(
            epc.fields.toggleField(
              'content.actions.addConversation.enabled',
              'After adding a conversation',
            ),
            {
              action: () => ({
                id: ITEMS_CONTAINER_ACTION_ADD_CONVERSATION,
                inputBindingKey: 'conversations',
              }),
              workflowId: (model: PropsOf<'chat'>) =>
                model.content?.actions?.addConversation?.targetId,
              conditionsPath: 'content.actions.addConversation.visibilityConditions',
              conditionsExpressionPath: 'content.actions.addConversation.visibilityExpression',
              conditionsConfig: {
                title: 'Add conversation visibility',
                scope: 'component',
              },
            },
          ),
          epc.utils.adornField<'toggle', PropertiesConfigInputOptionsToggle>(
            epc.fields.toggleField(
              'content.actions.editConversation.enabled',
              'After editing a conversation',
            ),
            {
              action: () => ({
                id: ITEMS_CONTAINER_ACTION_EDIT_CONVERSATION,
                inputBindingKey: 'conversations',
              }),
              workflowId: (model: PropsOf<'chat'>) =>
                model.content?.actions?.editConversation?.targetId,
              conditionsPath: 'content.actions.editConversation.visibilityConditions',
              conditionsExpressionPath: 'content.actions.editConversation.visibilityExpression',
              conditionsConfig: {
                title: 'Edit conversation visibility',
                scope: 'component',
              },
            },
          ),
          // epc.utils.adornField<'toggle', PropertiesConfigInputOptionsToggle>(
          //   epc.fields.toggleField('content.actions.deleteConversation.enabled', 'Delete conversation'),
          //   {
          //     action: () => ({
          //       id: ITEMS_CONTAINER_ACTION_DELETE_CONVERSATION,
          //       inputBindingKey: 'conversations',
          //     }),
          //     workflowId: (model: PropsOf<'chat'>) => model.content?.actions?.deleteConversation?.targetId,
          //   },
          // ),
          epc.utils.adornField<'toggle', PropertiesConfigInputOptionsToggle>(
            epc.fields.toggleField(
              'content.actions.leaveConversation.enabled',
              'After leaving a conversation',
            ),
            {
              action: () => ({
                id: ITEMS_CONTAINER_ACTION_LEAVE_CONVERSATION,
                inputBindingKey: 'conversations',
              }),
              workflowId: (model: PropsOf<'chat'>) =>
                model.content?.actions?.leaveConversation?.targetId,
              conditionsPath: 'content.actions.leaveConversation.visibilityConditions',
              conditionsExpressionPath: 'content.actions.leaveConversation.visibilityExpression',
              conditionsConfig: {
                title: 'Leave conversation visibility',
                scope: 'component',
              },
            },
          ),
        ],
        {
          lockWhen: (model: ChatPropsModel) => {
            const requiredKeys = ['messages', 'members', 'conversations', 'conversationsInfo'];
            const keysPresent = requiredKeys.every((requiredKey) =>
              model.data?.find(({ key, enabled }) => key === requiredKey && enabled),
            );
            return keysPresent ? undefined : 'Configure Data Source to enable Actions';
          },
        },
      ),
      epc.subsections.visibilitySubsection(),
      epc.subsections.subsection('content.chat', 'Size', [
        epc.root.elementWidthField({ min: 450, max: 1000, unit: 'px', step: 1 }, WIDTH_OPTIONS),
        epc.root.elementHeightField({ min: 450, max: 1000, unit: 'px', step: 1 }, HEIGHT_OPTIONS),
      ]),
      epc.subsections.titleContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'General', [
          epc.fields.colorField('style.general.background', 'Background', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.general.outline', 'Outline', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.general.accent', 'Accent', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.general.accentLabel', 'Accent label', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.radiusField('style.general.radius', 'Radius'),
          ...epc.fields.visualEffectField('style.general.visualEffect'),
        ]),
        epc.subsections.subsection('style.participants', 'Chat Participants', [
          epc.fields.colorField('style.participants.label', 'Label', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.participants.hoverBackground', 'Hover background', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.participants.selectedBackground', 'Selected background', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),
        epc.subsections.subsection('style.messages', 'Chat Messages', [
          epc.fields.colorField('style.messages.myText', 'My text', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.messages.myBackground', 'My background', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.messages.othersText', 'Others text', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.messages.othersBackground', 'Others background', {
            allowGradient: false,
            allowNone: false,
          }),
          ...epc.fields.fontFamilyAndWeightFields(
            'style.messages.fontFamily',
            'style.messages.fontWeight',
          ),
          epc.utils.observedField(
            epc.fields.sliderField('style.messages.fontSize', 'Size', [
              { min: 8, max: 72, unit: 'px' },
            ]),
            (props: ChatPropsModel, value: ValueWithUnitOfMeasure) =>
              validateLineHeightWithFontSize(props, value, 'style.messages.lineHeight'),
          ),
          epc.utils.observedField(
            epc.fields.sliderField('style.messages.lineHeight', 'Line height', [
              { min: 8, max: 72, unit: 'px' },
            ]),
            (props: ChatPropsModel, value: ValueWithUnitOfMeasure) =>
              validateFontSizeWithLineHeight(props, value, 'style.messages.fontSize'),
          ),
        ]),
        epc.subsections.subsection('style.input', 'Message Input', [
          epc.fields.colorField('style.input.text', 'Text', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.input.background', 'Background', {
            allowGradient: false,
            allowNone: true,
          }),
          epc.fields.colorField('style.input.outline', 'Outline', {
            allowGradient: false,
            allowNone: true,
          }),
          ...epc.fields.fontFamilyAndWeightFields(
            'style.input.fontFamily',
            'style.input.fontWeight',
          ),
          epc.utils.observedField(
            epc.fields.sliderField('style.input.fontSize', 'Size', [
              { min: 8, max: 72, unit: 'px' },
            ]),
            (props: ChatPropsModel, value: ValueWithUnitOfMeasure) =>
              validateLineHeightWithFontSize(props, value, 'style.input.lineHeight'),
          ),
          epc.utils.observedField(
            epc.fields.sliderField('style.input.lineHeight', 'Line height', [
              { min: 8, max: 72, unit: 'px' },
            ]),
            (props: ChatPropsModel, value: ValueWithUnitOfMeasure) =>
              validateFontSizeWithLineHeight(props, value, 'style.input.fontSize'),
          ),
        ]),
        epc.subsections.titleStyleSubsection({
          enabled: 'content.title.enabled',
          color: 'style.general.title',
          fontFamily: 'style.titleBlock.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'style.titleBlock.fontStyle',
          fontSize: 'style.titleBlock.fontSize',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.titleBlock.iconSize',
        }),
      ],
      true,
    ),
  ]);
}
